import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight } from '@fortawesome/pro-solid-svg-icons';
import Currency from '@v2/shared/components/Currency';
import ProductImage from '@v2/shared/components/ProductImage';
import { useSite } from '@v2/shared/lib/site';
import Box from './Box';
import useAutoRenewalUrls from './urls';
import { AutoRenewalDetail } from './types';

type Props = {
  autoRenewal: AutoRenewalDetail;
  /** The confirm page, with `:id` and `:hash` where the transaction's go. */
  confirmUrlTemplate: string;
};

/**
 * The title we can put a customer onto when theirs has gone away.
 *
 * Everything it needs is optional on the subscription — the alternate title, its term,
 * and the upcoming transaction the offer is addressed by — because most subscriptions
 * have no alternate to offer.
 *
 * The price is the one part that is allowed to be missing. It is display, while the
 * transaction's id and hash are what the "Subscribe Now" link is built from — so an
 * unpriced offer loses its price line and keeps its link, rather than the customer being
 * told their magazine is gone and offered no way to move.
 */
export default function AlternateProductOffer({ autoRenewal, confirmUrlTemplate }: Props) {
  const { confirmUrl } = useAutoRenewalUrls();
  const site = useSite();

  const product = autoRenewal.alternate_product;
  const sku = autoRenewal.alternate_sku;
  const upcoming = autoRenewal.upcoming_transaction;

  if (!product || !sku || !upcoming) {
    return null;
  }

  return (
    <Box>
      <div className="grid grid-cols-6 gap-3">
        <div className="col-span-2 md:col-span-1">
          <ProductImage product={product} className="w-full" />
        </div>

        {/*
          * Four of six on a phone. The source says `col-span-12` here, inside a
          * `grid-cols-6` — a twelve-column span copied into a six-column grid, which
          * makes the browser invent implicit tracks and lays the row out to a width
          * nothing else on the page uses. Two and four is what the rest of this page
          * does with a cover beside its copy.
          */}
        <div className="col-span-4 md:col-span-5">
          <h3 className="font-semibold mt-0">Switch to {product.name} instead</h3>
          <p className="font-medium text-sm text-slate-700 mb-6">
            As a loyal customer, you may take advantage of this exclusive special offer to {product.name}.
          </p>

          <div className="mb-6">
            <div className="font-medium text-slate-700 text-sm">{sku.text.split('/')[0].trim()}</div>
            {upcoming.price != null && (
              <>
                <div>
                  <span className="font-bold tracking-tight text-lg">
                    <Currency value={upcoming.price} currencyCode={site.currency} />
                  </span>{' '}
                  <s className="line-through font-medium text-slate-500 text-xs">
                    <Currency value={sku.price} currencyCode={site.currency} />
                  </s>
                </div>
                <div className="font-medium text-xs text-slate-600 italic">+ applicable taxes</div>
              </>
            )}
          </div>

          <a
            href={confirmUrl(confirmUrlTemplate, upcoming)}
            className="font-semibold text-sm text-sky-600 hover:text-sky-700 active:text-sky-800 hover:underline"
          >
            Subscribe Now
            <FontAwesomeIcon icon={faAngleRight} className="ml-1" />
          </a>
        </div>
      </div>
    </Box>
  );
}
